import type { ReactNode } from 'react';

import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { APIProvider as GoogleMapsAPIProvider } from '@vis.gl/react-google-maps';

import { MaintenanceOverlay } from '../components/MaintenanceOverlay';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { EntityChangeProvider } from '../contexts/EntityChangeContext';
import { EntityProvider } from '../contexts/EntityContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import { TitleProvider } from '../contexts/TitleContext';
import { queryClient } from '../lib/trpc';
import { theme } from '../theme';

type RuntimeAppConfig = NonNullable<Window['APP_CONFIG']>;

type AppProvidersProps = {
  appConfig: RuntimeAppConfig;
  children: ReactNode;
};

function AuthScopedEntityProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return <EntityProvider key={user?.id ?? 'unauthenticated'}>{children}</EntityProvider>;
}

export function AppProviders({ appConfig, children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={appConfig.GOOGLE_CLIENT_ID}>
        <AuthProvider queryClient={queryClient}>
          <NotificationProvider>
            <EntityChangeProvider>
              <TitleProvider>
                <MantineProvider theme={theme}>
                  <DatesProvider settings={{ firstDayOfWeek: 0, weekendDays: [0, 6] }}>
                    <GoogleMapsAPIProvider
                      apiKey={appConfig.GOOGLE_MAPS_API_KEY}
                      libraries={['maps', 'places']}
                    >
                      <AuthScopedEntityProvider>
                        {children}
                        <MaintenanceOverlay />
                      </AuthScopedEntityProvider>
                    </GoogleMapsAPIProvider>
                  </DatesProvider>
                </MantineProvider>
              </TitleProvider>
            </EntityChangeProvider>
          </NotificationProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
}
